import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import type { AgentTemplate, TemplateCategory } from "../api/client.js";
import {
  listAgentTemplates,
  installAgentTemplate,
  deleteAgentTemplate,
} from "../api/client.js";

// ── Category metadata ────────────────────────────────────────────────────────

const CATEGORY_META: Record<TemplateCategory, { label: string; icon: string }> = {
  assistant: { label: "Assistant", icon: "chat-bubble" },
  code: { label: "Code", icon: "code-bracket" },
  security: { label: "Security", icon: "shield" },
  data: { label: "Data", icon: "chart-bar" },
  research: { label: "Research", icon: "magnifying-glass" },
  custom: { label: "Custom", icon: "puzzle-piece" },
};

const ALL_CATEGORIES: TemplateCategory[] = ["assistant", "code", "security", "data", "research", "custom"];

// ── Main Component ──────────────────────────────────────────────────────────

export function AgentCatalog() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<AgentTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState<TemplateCategory | "all">("all");
  const [search, setSearch] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState<AgentTemplate | null>(null);
  const [installModal, setInstallModal] = useState<AgentTemplate | null>(null);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (category !== "all") params.category = category;
      if (search) params.search = search;
      const { templates: data } = await listAgentTemplates(params);
      setTemplates(data);
    } catch {
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [category, search]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  return (
    <div className="flex h-full">
      {/* Main content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-ink-950">Agent Catalog</h1>
          <p className="mt-1 text-sm text-ink-500">
            Browse and install pre-built agent templates
          </p>
        </div>

        {/* Filters */}
        <div className="mb-6 flex items-center gap-4">
          {/* Search */}
          <input
            type="text"
            placeholder="Search templates..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-64 rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
          />

          {/* Category filter chips */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setCategory("all")}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                category === "all"
                  ? "bg-ink-950 text-white"
                  : "bg-surface-2 text-ink-600 hover:bg-surface-3"
              }`}
            >
              All
            </button>
            {ALL_CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  category === cat
                    ? "bg-ink-950 text-white"
                    : "bg-surface-2 text-ink-600 hover:bg-surface-3"
                }`}
              >
                {CATEGORY_META[cat].label}
              </button>
            ))}
          </div>
        </div>

        {/* Template grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-ink-300 border-t-accent-600" />
          </div>
        ) : templates.length === 0 ? (
          <div className="py-20 text-center text-ink-400">
            No templates found{search ? ` matching "${search}"` : ""}.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {templates.map((tmpl) => (
              <TemplateCard
                key={tmpl.id}
                template={tmpl}
                onSelect={() => setSelectedTemplate(tmpl)}
                onInstall={() => setInstallModal(tmpl)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Detail panel (sidebar) */}
      {selectedTemplate && (
        <TemplateDetailPanel
          template={selectedTemplate}
          onClose={() => setSelectedTemplate(null)}
          onInstall={() => {
            setInstallModal(selectedTemplate);
            setSelectedTemplate(null);
          }}
          onDelete={async () => {
            await deleteAgentTemplate(selectedTemplate.id);
            setSelectedTemplate(null);
            fetchTemplates();
          }}
        />
      )}

      {/* Install modal */}
      {installModal && (
        <InstallTemplateModal
          template={installModal}
          onClose={() => setInstallModal(null)}
          onInstalled={(agentId) => {
            setInstallModal(null);
            fetchTemplates();
            navigate(`/agents/${agentId}`);
          }}
        />
      )}
    </div>
  );
}

// ── Template Card ────────────────────────────────────────────────────────────

function TemplateCard({
  template,
  onSelect,
  onInstall,
}: {
  template: AgentTemplate;
  onSelect: () => void;
  onInstall: () => void;
}) {
  const categoryMeta = CATEGORY_META[template.category] ?? CATEGORY_META.custom;

  return (
    <div
      className="group cursor-pointer rounded-xl border border-ink-100 bg-white p-5 transition-shadow hover:shadow-md"
      onClick={onSelect}
    >
      <div className="mb-3 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <CategoryIcon icon={categoryMeta.icon} />
          <div>
            <h3 className="font-semibold text-ink-900">{template.name}</h3>
            <span className="text-xs text-ink-400">{categoryMeta.label}</span>
          </div>
        </div>
        {template.featured && (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
            Featured
          </span>
        )}
      </div>

      <p className="mb-4 line-clamp-2 text-sm text-ink-500">
        {template.description ?? "No description"}
      </p>

      <div className="mb-3 flex flex-wrap gap-1.5">
        {template.tags.slice(0, 4).map((tag) => (
          <span
            key={tag}
            className="rounded bg-surface-2 px-1.5 py-0.5 text-[10px] font-medium text-ink-500"
          >
            {tag}
          </span>
        ))}
      </div>

      <div className="flex items-center justify-between border-t border-ink-50 pt-3">
        <div className="flex items-center gap-3 text-xs text-ink-400">
          <span>v{template.version}</span>
          <span>{template.model.split("-").slice(0, 2).join("-")}</span>
          {template.skills.length > 0 && (
            <span>{template.skills.length} skill{template.skills.length !== 1 ? "s" : ""}</span>
          )}
          {template.installCount > 0 && (
            <span>{template.installCount} install{template.installCount !== 1 ? "s" : ""}</span>
          )}
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onInstall();
          }}
          className="rounded-lg bg-accent-600 px-3 py-1 text-xs font-medium text-white opacity-0 transition-opacity group-hover:opacity-100 hover:bg-accent-700"
        >
          Install
        </button>
      </div>
    </div>
  );
}

// ── Template Detail Panel ───────────────────────────────────────────────────

function TemplateDetailPanel({
  template,
  onClose,
  onInstall,
  onDelete,
}: {
  template: AgentTemplate;
  onClose: () => void;
  onInstall: () => void;
  onDelete: () => void;
}) {
  return (
    <aside className="w-96 shrink-0 overflow-auto border-l border-ink-100 bg-white p-6">
      <div className="mb-4 flex items-start justify-between">
        <h2 className="text-lg font-bold text-ink-950">{template.name}</h2>
        <button onClick={onClose} className="text-ink-400 hover:text-ink-600">
          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>

      <p className="mb-4 text-sm text-ink-500">{template.description ?? "No description"}</p>

      <div className="mb-4 space-y-2">
        <DetailRow label="Category" value={CATEGORY_META[template.category]?.label ?? template.category} />
        <DetailRow label="Model" value={template.model} />
        <DetailRow label="Policy" value={template.toolPolicyProfile} />
        <DetailRow label="Author" value={template.author} />
        <DetailRow label="Version" value={template.version} />
        <DetailRow label="Installs" value={String(template.installCount)} />
        <DetailRow label="SHA-256" value={template.sha256.slice(0, 16) + "..."} />
      </div>

      {template.tags.length > 0 && (
        <div className="mb-4">
          <label className="mb-1 block text-xs font-medium text-ink-400">Tags</label>
          <div className="flex flex-wrap gap-1.5">
            {template.tags.map((tag) => (
              <span key={tag} className="rounded bg-surface-2 px-2 py-0.5 text-xs text-ink-600">
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {template.tools.length > 0 && (
        <div className="mb-4">
          <label className="mb-1 block text-xs font-medium text-ink-400">Required Tools</label>
          <div className="flex flex-wrap gap-1.5">
            {template.tools.map((tool) => (
              <span key={tool} className="rounded bg-blue-50 px-2 py-0.5 text-xs text-blue-700">
                {tool}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="mb-4">
        <label className="mb-1 block text-xs font-medium text-ink-400">System Prompt Preview</label>
        <pre className="max-h-48 overflow-auto rounded-lg bg-surface-1 p-3 text-xs text-ink-700">
          {template.systemPrompt}
        </pre>
      </div>

      <div className="flex gap-2">
        <button
          onClick={onInstall}
          className="flex-1 rounded-lg bg-accent-600 py-2 text-sm font-medium text-white hover:bg-accent-700"
        >
          Install
        </button>
        <button
          onClick={onDelete}
          className="rounded-lg border border-red-200 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
        >
          Delete
        </button>
      </div>
    </aside>
  );
}

// ── Install Modal ───────────────────────────────────────────────────────────

function InstallTemplateModal({
  template,
  onClose,
  onInstalled,
}: {
  template: AgentTemplate;
  onClose: () => void;
  onInstalled: (agentId: string) => void;
}) {
  const [name, setName] = useState(template.name);
  const [model, setModel] = useState(template.model);
  const [policy, setPolicy] = useState(template.toolPolicyProfile);
  const [installing, setInstalling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInstall = async () => {
    setInstalling(true);
    setError(null);
    try {
      const result = await installAgentTemplate(template.id, {
        name: name !== template.name ? name : undefined,
        model: model !== template.model ? model : undefined,
        toolPolicyProfile: policy !== template.toolPolicyProfile ? (policy as any) : undefined,
      });
      onInstalled(result.agent.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Installation failed");
    } finally {
      setInstalling(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <h2 className="mb-1 text-lg font-bold text-ink-950">Install Template</h2>
        <p className="mb-4 text-sm text-ink-500">
          Create a new agent from "{template.name}"
        </p>

        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-600">Agent Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-ink-600">Model</label>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="w-full rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
            >
              <option value="claude-sonnet-4-5-20250929">Claude Sonnet 4.5</option>
              <option value="claude-opus-4-6">Claude Opus 4.6</option>
              <option value="gpt-4o">GPT-4o</option>
              <option value="gpt-4o-mini">GPT-4o Mini</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-ink-600">Tool Policy</label>
            <select
              value={policy}
              onChange={(e) => setPolicy(e.target.value)}
              className="w-full rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
            >
              <option value="read-only">Read-only</option>
              <option value="standard">Standard</option>
              <option value="privileged">Privileged</option>
            </select>
          </div>

          {template.skills.length > 0 && (
            <div>
              <label className="mb-1 block text-xs font-medium text-ink-600">Skills to Bind</label>
              <div className="flex flex-wrap gap-1.5">
                {template.skills.map((skill) => (
                  <span key={skill} className="rounded bg-green-50 px-2 py-0.5 text-xs text-green-700">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="mt-3 rounded-lg bg-red-50 p-2 text-xs text-red-600">{error}</div>
        )}

        <div className="mt-6 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 rounded-lg border border-ink-200 py-2 text-sm font-medium text-ink-600 hover:bg-surface-2"
          >
            Cancel
          </button>
          <button
            onClick={handleInstall}
            disabled={installing || !name.trim()}
            className="flex-1 rounded-lg bg-accent-600 py-2 text-sm font-medium text-white hover:bg-accent-700 disabled:opacity-50"
          >
            {installing ? "Installing..." : "Install Agent"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs font-medium text-ink-400">{label}</span>
      <span className="text-xs text-ink-700">{value}</span>
    </div>
  );
}

function CategoryIcon({ icon }: { icon: string }) {
  const baseClass = "flex h-10 w-10 items-center justify-center rounded-lg";
  switch (icon) {
    case "chat-bubble":
      return (
        <div className={`${baseClass} bg-blue-50`}>
          <svg className="h-5 w-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
            <path d="M2 5a2 2 0 012-2h12a2 2 0 012 2v7a2 2 0 01-2 2H6l-4 4V5z" />
          </svg>
        </div>
      );
    case "code-bracket":
      return (
        <div className={`${baseClass} bg-purple-50`}>
          <svg className="h-5 w-5 text-purple-600" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </div>
      );
    case "shield":
      return (
        <div className={`${baseClass} bg-red-50`}>
          <svg className="h-5 w-5 text-red-600" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 1.944A11.954 11.954 0 012.166 5C2.056 5.649 2 6.319 2 7c0 5.225 3.34 9.67 8 11.317C14.66 16.67 18 12.225 18 7c0-.682-.057-1.35-.166-2.001A11.954 11.954 0 0110 1.944zM11 14a1 1 0 11-2 0 1 1 0 012 0zm0-7a1 1 0 10-2 0v3a1 1 0 102 0V7z" clipRule="evenodd" />
          </svg>
        </div>
      );
    case "chart-bar":
      return (
        <div className={`${baseClass} bg-green-50`}>
          <svg className="h-5 w-5 text-green-600" viewBox="0 0 20 20" fill="currentColor">
            <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
          </svg>
        </div>
      );
    case "magnifying-glass":
      return (
        <div className={`${baseClass} bg-amber-50`}>
          <svg className="h-5 w-5 text-amber-600" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
          </svg>
        </div>
      );
    default:
      return (
        <div className={`${baseClass} bg-ink-50`}>
          <svg className="h-5 w-5 text-ink-500" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10 3.5a1.5 1.5 0 013 0V4a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-.5a1.5 1.5 0 000 3h.5a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-.5a1.5 1.5 0 00-3 0v.5a1 1 0 01-1 1H6a1 1 0 01-1-1v-3a1 1 0 00-1-1h-.5a1.5 1.5 0 010-3H4a1 1 0 001-1V6a1 1 0 011-1h3a1 1 0 001-1v-.5z" />
          </svg>
        </div>
      );
  }
}
